/* @ts-ignore */
import queryStringHandler from "query-string-handler";
import { scrollToElement } from "@/components/global/scroll-to-element";

interface PartnerImage {
  altText?: string;
  url?: string;
}

interface PartnerItem {
  name?: string;
  regions?: string[];
  url?: string;
  image?: PartnerImage | null;
}

interface PartnerFacet {
  label?: string;
  isDisabled?: boolean;
}

interface PartnerFacetCollection {
  title?: string;
  facets?: PartnerFacet[];
}

interface PartnerSearchResponse {
  currentPage: number;
  totalPages: number;
  items?: PartnerItem[] | null;
  facetsCollections?: PartnerFacetCollection[] | null;
}

const escapeHtml = (text: string): string => {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
};

const buildPartnerListingCardInnerHtml = (item: PartnerItem): string => {
  const nameHtml = item.name ? escapeHtml(item.name) : "";
  const imgUrl = item.image?.url ? escapeHtml(item.image.url) : "";
  const imgAlt = item.image?.altText ? escapeHtml(item.image.altText) : "";

  const imageBlock = imgUrl !== "" ? `<img src="${imgUrl}" alt="${imgAlt}" class="max-h-full w-full max-w-[280px] object-contain md:max-h-[220px]" loading="lazy" />` : "";
  const regionBlock = item.regions && item.regions.length > 0 ? `<div class="body-sm font-medium text-primary-600 group-data-[colormode=White]/block:text-white group-data-[colormode=Black]/block:text-primary-600">${item.regions.join(" | ")}</div>` : "";
  const nameBlock = nameHtml ? `<h3 class="h5-mobile lg:h5 text-neutral-500 group-data-[colormode=White]/block:text-white group-data-[colormode=Black]/block:text-neutral-500">${nameHtml}</h3>` : "";

  return `<div class="flex aspect-square w-full min-h-0 flex-col rounded-3xl bg-white outline outline-neutral-100 outline-offset-[-1px] px-6 py-6 md:px-10 md:py-8">
    <div class="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden pb-4 md:min-h-[100px] md:pb-6">
      ${imageBlock}
    </div>
    <div class="flex w-full min-w-0 shrink-0 flex-col gap-2">
      ${regionBlock}
      ${nameBlock}
    </div>
  </div>`;
};

const buildPartnerCardHtml = (item: PartnerItem): string => {
  const inner = buildPartnerListingCardInnerHtml(item);
  const itemShell = `<div class="js-result-item col-span-1">`;
  const linkClass = "block h-full text-neutral-900 no-underline hover:no-underline focus:no-underline visited:text-neutral-900";

  if (item.url) {
    const href = escapeHtml(item.url);
    return `${itemShell}<a href="${href}" class="${linkClass}">${inner}</a></div>`;
  }

  return `${itemShell}${inner}</div>`;
};

const syncFacetDisabledState = (root: HTMLElement, collections: PartnerFacetCollection[] | null | undefined) => {
  if (!collections?.length) {
    return;
  }

  for (const col of collections) {
    const queryKey = col.title === "Region" ? "rg" : col.title === "Product" ? "pd" : null;
    if (!queryKey || !col.facets?.length) {
      continue;
    }

    const filterRoot = root.querySelector(`li[data-query="${queryKey}"]`);
    if (filterRoot) {
      filterRoot.querySelectorAll<HTMLButtonElement>(".js-filter-btn").forEach((btn) => {
        const val = btn.getAttribute("data-value") ?? "";
        const facet = col.facets!.find((f) => (f.label ?? "") === val);
        if (facet) {
          btn.disabled = !!facet.isDisabled;
        }
      });
    }

    root.querySelectorAll<HTMLButtonElement>(`.js-mobile-filter-btn[data-query="${queryKey}"]`).forEach((btn) => {
      const val = btn.getAttribute("data-value") ?? "";
      const facet = col.facets!.find((f) => (f.label ?? "") === val);
      if (facet) {
        btn.disabled = !!facet.isDisabled;
      }
    });
  }
};

const PartnerListingPage = () => {
  const partnerListing = document.querySelector<HTMLDivElement>(".js-partner-listing-page");
  if (!partnerListing) {
    return;
  }

  const filtersMenu = partnerListing.querySelector<HTMLDivElement>(".js-filters-mobile-menu");
  const fixedMenuWrapper = filtersMenu?.querySelector<HTMLDivElement>(".js-fixed-menu");
  const selectFilters = partnerListing.querySelectorAll<HTMLLIElement>(".js-filter-item");
  const resultsWrapper = partnerListing.querySelector<HTMLDivElement>(".js-results-wrapper");
  const resultsGrid = partnerListing.querySelector<HTMLDivElement>(".js-results-grid");
  const paginationWrapper = partnerListing.querySelector<HTMLDivElement>(".js-pagination-wrapper");
  const prevPageBtn = partnerListing.querySelector<HTMLButtonElement>(".js-prev-btn");
  const nextPageBtn = partnerListing.querySelector<HTMLButtonElement>(".js-next-btn");
  const clearBtns = partnerListing.querySelectorAll<HTMLButtonElement>(".js-clear-btn");
  const openBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-open-menu-btn");
  const cancelBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-cancel-btn");
  const applyBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-apply-btn");
  const mobileFilterItems = filtersMenu?.querySelectorAll<HTMLDivElement>(".js-mobile-filter-item");

  let page = parseInt(queryStringHandler.getValue("pg") || partnerListing.getAttribute("data-current-page") || "1", 10);
  let totalPages = parseInt(partnerListing.getAttribute("data-total-pages") || "0", 10);
  let region = queryStringHandler.getValue("rg") || "";
  let product = queryStringHandler.getValue("pd") || "";
  let savedRegion = "";
  let savedProduct = "";
  let savedPage = 0;

  const handleUpdateClearBtn = () => {
    const rg = queryStringHandler.getValue("rg");
    const pd = queryStringHandler.getValue("pd");
    const show = rg || pd ? "true" : "false";
    clearBtns.forEach((btn) => btn.setAttribute("data-show", show));
  };

  const handleDisabledPagination = () => {
    if (totalPages <= 1) {
      paginationWrapper?.setAttribute("data-hide", "true");
      return;
    }
    paginationWrapper?.setAttribute("data-hide", "false");
    if (prevPageBtn) {
      prevPageBtn.disabled = page <= 1;
    }
    if (nextPageBtn) {
      nextPageBtn.disabled = page >= totalPages;
    }
  };

  const handleRemoveCards = () => {
    if (!resultsGrid) {
      return;
    }
    resultsGrid.innerHTML = "";
  };

  const handleNewCards = (items: PartnerItem[]) => {
    if (!resultsGrid) {
      return;
    }
    if (items.length > 0) {
      partnerListing.setAttribute("data-noresults", "false");
      resultsWrapper?.setAttribute("data-results", "true");
      items.forEach((item) => {
        resultsGrid.insertAdjacentHTML("beforeend", buildPartnerCardHtml(item));
      });
    } else {
      partnerListing.setAttribute("data-noresults", "true");
      resultsWrapper?.setAttribute("data-results", "false");
    }
  };

  const handleFetchResults = async (pg: number, rg: string, pd: string) => {
    try {
      partnerListing.setAttribute("data-loading", "true");

      const params = new URLSearchParams();
      params.set("pg", String(pg));
      if (rg) {
        params.set("rg", rg);
      }
      if (pd) {
        params.set("pd", pd);
      }

      const response = await fetch(`/api/search/partner-detail?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data: PartnerSearchResponse = await response.json();

      handleRemoveCards();

      const items = data.items ?? [];
      handleNewCards(items);

      if (data.facetsCollections) {
        syncFacetDisabledState(partnerListing, data.facetsCollections);
      }

      page = data.currentPage;
      totalPages = data.totalPages;
      partnerListing.setAttribute("data-current-page", String(page));
      partnerListing.setAttribute("data-total-pages", String(totalPages));

      handleUpdateSelectValue("rg", rg);
      handleUpdateSelectValue("pd", pd);
      handleUpdateClearBtn();
      handleDisabledPagination();
      partnerListing.setAttribute("data-loading", "false");
    } catch (err) {
      console.error("Partner listing fetch error:", err);
      partnerListing.setAttribute("data-noresults", "true");
      partnerListing.setAttribute("data-loading", "false");
    }
  };

  const handleUpdateSelectValue = (selectedQuery: string, value: string) => {
    selectFilters.forEach((item) => {
      const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-text");
      const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
      const groupQuery = item.getAttribute("data-query");

      if (filterBtns.length > 0 && menuBtnSelectedText && groupQuery === selectedQuery.trim()) {
        let selectedIndex = 0;

        filterBtns.forEach((btn, index) => {
          if ((btn.getAttribute("data-value") ?? "") === value) {
            menuBtnSelectedText.innerText = btn.textContent || "";
            selectedIndex = index;
          }
        });

        filterBtns[selectedIndex].disabled = true;
      }
    });

    mobileFilterItems?.forEach((item) => {
      const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
      const groupQuery = item.getAttribute("data-group-query");
      const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");

      if (menuBtnSelectedText && filterBtns.length > 0 && groupQuery === selectedQuery.trim()) {
        let selectedIndex = 0;

        filterBtns.forEach((btn, index) => {
          if ((btn.getAttribute("data-value") ?? "") === value) {
            menuBtnSelectedText.innerText = btn.textContent || "";
            selectedIndex = index;
          }
        });

        filterBtns[selectedIndex].disabled = true;
      }
    });
  };

  const handleUpdateQuery = (query: string, value: string) => {
    queryStringHandler.replace({ [query]: value });

    if (query === "rg") {
      region = value;
    } else if (query === "pd") {
      product = value;
    }

    if (page !== 1 && (query === "rg" || query === "pd")) {
      page = 1;
      queryStringHandler.replace({ pg: "1" });
    }

    handleFetchResults(page, region, product);
  };

  const handleClearFilters = () => {
    region = "";
    product = "";
    page = 1;
    queryStringHandler.replace({ rg: "" });
    queryStringHandler.replace({ pd: "" });
    queryStringHandler.replace({ pg: "1" });
    handleFetchResults(page, region, product);
  };

  const handleScrollToTop = () => {
    scrollToElement("partner-listing-page");
  };

  const handlePreviousPage = async () => {
    if (page <= 1) {
      return;
    }
    page -= 1;
    queryStringHandler.replace({ pg: String(page) });
    await handleFetchResults(page, region, product);
    handleScrollToTop();
  };

  const handleNextPage = async () => {
    if (page >= totalPages) {
      return;
    }
    page += 1;
    queryStringHandler.replace({ pg: String(page) });
    await handleFetchResults(page, region, product);
    handleScrollToTop();
  };

  const handleSetSavedData = () => {
    savedRegion = region;
    savedProduct = product;
    savedPage = page;
  };

  const handleOpenMenu = () => {
    if (filtersMenu && fixedMenuWrapper) {
      filtersMenu.setAttribute("data-open", "true");
      document.body.style.overflow = "hidden";
      fixedMenuWrapper.style.height = `${window.innerHeight}px`;
      handleSetSavedData();
    }
  };

  const handleCloseMenu = () => {
    if (filtersMenu && fixedMenuWrapper) {
      filtersMenu.setAttribute("data-open", "false");
      fixedMenuWrapper.style.height = "";
      document.body.style.overflow = "";
    }
  };

  const handleApplyChanges = () => {
    handleCloseMenu();
  };

  const handleResize = () => {
    if (filtersMenu?.getAttribute("data-open") === "true") {
      handleCloseMenu();
    }
  };

  const handleCancelChanges = () => {
    handleCloseMenu();
    if (savedRegion !== region || savedProduct !== product || savedPage !== page) {
      queryStringHandler.replace({ rg: savedRegion });
      queryStringHandler.replace({ pd: savedProduct });
      queryStringHandler.replace({ pg: String(savedPage) });
      page = savedPage;
      region = savedRegion;
      product = savedProduct;
      handleFetchResults(savedPage, savedRegion, savedProduct);
    }
  };

  mobileFilterItems?.forEach((item) => {
    const selectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
    const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");
    const accordionBtn = item.querySelector<HTMLButtonElement>(".js-accordion-btn");
    const accordionContentWrapper = item.querySelector<HTMLDivElement>(".js-accordion-content-wrapper");

    const handleCloseAccordion = () => {
      if (accordionBtn && accordionContentWrapper) {
        accordionBtn.setAttribute("aria-expanded", "false");
        accordionContentWrapper.style.maxHeight = "";
      }
    };

    filterBtns.forEach((btn) => {
      const groupQuery = btn.getAttribute("data-query");
      const queryValue = btn.getAttribute("data-value");

      btn.addEventListener("click", () => {
        if (selectedText) {
          selectedText.innerText = btn.textContent || "";
        }
        if (groupQuery) {
          handleUpdateQuery(groupQuery, queryValue || "");
          handleCloseAccordion();
        }
      });
    });
  });

  if (prevPageBtn) {
    prevPageBtn.addEventListener("click", handlePreviousPage);
  }
  if (nextPageBtn) {
    nextPageBtn.addEventListener("click", handleNextPage);
  }

  clearBtns.forEach((btn) => btn.addEventListener("click", handleClearFilters));

  selectFilters.forEach((item) => {
    const menuBtn = item.querySelector<HTMLButtonElement>(".js-select-menu-btn");
    const menuBtnSelectedText = menuBtn?.querySelector<HTMLSpanElement>(".js-selected-text");
    const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
    const groupQuery = item.getAttribute("data-query");

    const handleTabIndex = () => {
      filterBtns.forEach((btn) => {
        btn.tabIndex = item.getAttribute("data-open") === "true" ? 0 : -1;
      });
    };

    const handleToggleMenu = () => {
      const open = item.getAttribute("data-open") === "true";
      item.setAttribute("data-open", open ? "false" : "true");
      handleTabIndex();
    };

    filterBtns.forEach((btn, i) => {
      const value = btn.getAttribute("data-value");
      const text = btn.textContent;

      if (groupQuery && text) {
        btn.addEventListener("click", () => {
          if (menuBtnSelectedText) {
            menuBtnSelectedText.innerText = text;
          }
          handleToggleMenu();
          handleUpdateQuery(groupQuery, value || "");
        });

        btn.addEventListener("keydown", (e: KeyboardEvent) => {
          if (e.code === "Escape") {
            item.setAttribute("data-open", "false");
            menuBtn?.focus();
            handleTabIndex();
          }
        });
      }

      if (i === filterBtns.length - 1) {
        btn.addEventListener("keydown", (e: KeyboardEvent) => {
          if (e.code === "Tab" && !e.shiftKey) {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        });
      }
    });

    if (menuBtn) {
      menuBtn.addEventListener("click", handleToggleMenu);
    }

    document.addEventListener("click", (e: MouseEvent) => {
      const target = e.target as Node;
      if (!item.contains(target) && item.getAttribute("data-open") === "true") {
        item.setAttribute("data-open", "false");
        handleTabIndex();
      }
    });
  });

  if (openBtn) {
    openBtn.addEventListener("click", handleOpenMenu);
  }
  if (cancelBtn) {
    cancelBtn.addEventListener("click", handleCancelChanges);
  }
  if (applyBtn) {
    applyBtn.addEventListener("click", handleApplyChanges);
  }

  window.addEventListener("resize", handleResize);

  document.addEventListener("DOMContentLoaded", () => {
    const pageQuery = queryStringHandler.getValue("pg") ? parseInt(queryStringHandler.getValue("pg")!, 10) : page;
    const regionQuery = queryStringHandler.getValue("rg") || region;
    const productQuery = queryStringHandler.getValue("pd") || product;

    if (pageQuery !== page || regionQuery !== region || productQuery !== product) {
      page = pageQuery;
      region = regionQuery;
      product = productQuery;
      handleFetchResults(page, region, product);
    } else {
      handleUpdateClearBtn();
      handleDisabledPagination();
      partnerListing.setAttribute("data-loading", "false");
    }
  });
};

export default PartnerListingPage;
